import React, { useEffect, useRef, useState, useCallback } from "react"
import Modal from "../../ui/Modal"
import Button from "../../ui/Button"
import { FaPrint, FaEnvelope } from "react-icons/fa"
import { openComprobanteWindow } from "./comprobantePOS"
import { FileCheck } from "lucide-react"
import type { Invoice } from "./Invoice"
import SendInvoiceModal, { InvoiceRecipientInfo } from "../modals/SendInvoiceModal"

interface PrintInvoiceModalProps {
  factura: Invoice | null
  isOpen: boolean
  onClose: () => void
  onSendEmail: (data: InvoiceRecipientInfo) => Promise<void>;
}

const PrintModal: React.FC<PrintInvoiceModalProps> = ({ factura, isOpen, onClose, onSendEmail }) => {
  const printButtonRef = useRef<HTMLButtonElement>(null)
  const [isSending, setIsSending] = useState(false)
  const [isSendModalOpen, setIsSendModalOpen] = useState(false)

  const handleSendEmailClick = () => {
    setIsSendModalOpen(true)
  }

  // Envolver en useCallback para que sea una dependencia estable
  const handlePrintClick = useCallback(() => {
    if (factura) {
      openComprobanteWindow(factura)
      setTimeout(() => {
        onClose()
      }, 500)
    }
  }, [factura, onClose]) // Dependencias necesarias

  const handleEmailSubmit = async (data: InvoiceRecipientInfo) => {
    setIsSending(true)
    try {
      await onSendEmail(data)
      setIsSendModalOpen(false)
      onClose()
    } catch (error) {
      console.error("Fallo al enviar correo:", error)
    } finally {
      setIsSending(false)
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setIsSending(false)
      setIsSendModalOpen(false) 
      return
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isSendModalOpen) return;
      
      e.stopPropagation()
      if (e.key === "Escape") {
        onClose()
      } else if (e.shiftKey && factura) {
        e.preventDefault()
        handlePrintClick()
      }
    }
    window.addEventListener("keydown", handleKeyDown, true)
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true)
    }
  }, [isOpen, onClose, factura, isSendModalOpen, handlePrintClick]) // Agregar handlePrintClick aquí

  if (!factura) return null

  return (
    <>
      <Modal
        isOpen={isOpen && !isSendModalOpen}
        onClose={onClose}
        title="Recibo de Venta"
        size="sm"
        footer={
          <div className="space-y-3">
            <div className="flex w-full gap-3">
              <Button
                variant="outline"
                icon={<FaEnvelope />}
                onClick={handleSendEmailClick}
                className="flex-1"
              >
                Enviar Correo
              </Button>
              <Button
                variant="primary"
                icon={<FaPrint />}
                onClick={handlePrintClick}
                ref={printButtonRef}
                className="flex-1"
              >
                Imprimir
              </Button>
            </div>
            <div className="px-1 pt-2 bg-gray-100 border-t border-gray-200 flex justify-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-blue-700 text-white text-xs font-bold rounded">ESC</span>
                <span>Salir</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-blue-700 text-white text-xs font-bold rounded">SHIFT</span>
                <span>Imprimir</span>
              </div>
            </div>
          </div>
        }
      >
        <div className="flex flex-col items-center justify-center text-center bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-3">
          <FileCheck className="w-12 h-12 text-blue-600" />
          <h3 className="text-lg font-semibold text-blue-800">¡Tu comprobante está listo!</h3>
          <p className="text-sm text-blue-700">Puedes imprimirlo, enviarlo por correo, o simplemente cerrar esta ventana.</p>
        </div>
      </Modal>
      
      <SendInvoiceModal
        isOpen={isOpen && isSendModalOpen}
        onClose={() => setIsSendModalOpen(false)}
        onSubmit={handleEmailSubmit}
        isSending={isSending}
      />
    </>
  )
}

export default PrintModal